import { useMutation } from '@apollo/client';
import { useCallback } from 'react';

import { REPORT_LOGIN_FAILURE } from '@neo.tax/shared/graphql/documents.graphql';
import {
  ClientReportableLoginMethod,
  ReportLoginFailureMutation,
  ReportLoginFailureMutationVariables,
} from '@neo.tax/shared/graphql/types';

/**
 * A callback that records a failed sign-in attempt through `reportLoginFailure`.
 *
 * Fire-and-forget: it never rejects and never blocks, so a slow or failed audit cannot change what the user
 * sees on a sign-in that has already ended.
 */
export const useReportLoginFailure = (): ((
  email: string,
  method: ClientReportableLoginMethod,
  rejectionReason: string,
) => void) => {
  const [reportLoginFailure] = useMutation<ReportLoginFailureMutation, ReportLoginFailureMutationVariables>(
    REPORT_LOGIN_FAILURE,
  );
  return useCallback(
    (email: string, method: ClientReportableLoginMethod, rejectionReason: string): void => {
      void (async (): Promise<void> => {
        try {
          await reportLoginFailure({ variables: { input: { email, method, rejectionReason } } });
        } catch {
          // Intentionally swallowed — auditing is best-effort and must not affect the sign-in UX.
        }
      })();
    },
    [reportLoginFailure],
  );
};
